import { api } from "@repo/convex/api";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { SettingsPageShell } from "@/features/settings";
import { useObservedMutation } from "@/hooks/use-observed-convex";

type NotificationKey =
  | "clarificationResponses"
  | "versionChanges"
  | "mentions"
  | "weeklyDigest"
  | "qualityIssues";

type NotificationRow = {
  key: NotificationKey;
  label: string;
  description: string;
  emailKey: keyof typeof defaultNotifications.email | null;
  inAppKey: keyof typeof defaultNotifications.inApp | null;
};

const defaultNotifications = {
  email: {
    clarificationResponses: true,
    versionChanges: true,
    mentions: true,
    weeklyDigest: false,
  },
  inApp: {
    clarificationResponses: true,
    versionChanges: true,
    mentions: true,
    qualityIssues: true,
  },
};

const notificationRows: NotificationRow[] = [
  {
    key: "clarificationResponses",
    label: "Clarification responses",
    description: "Updates when your clarification requests receive responses.",
    emailKey: "clarificationResponses",
    inAppKey: "clarificationResponses",
  },
  {
    key: "versionChanges",
    label: "Version changes",
    description: "Changes published to project versions you are involved in.",
    emailKey: "versionChanges",
    inAppKey: "versionChanges",
  },
  {
    key: "mentions",
    label: "Mentions",
    description: "When someone mentions you in comments or notes.",
    emailKey: "mentions",
    inAppKey: "mentions",
  },
  {
    key: "weeklyDigest",
    label: "Weekly digest",
    description: "A summary of important activity once per week.",
    emailKey: "weeklyDigest",
    inAppKey: null,
  },
  {
    key: "qualityIssues",
    label: "Quality issues",
    description: "Warnings about data quality or schema consistency.",
    emailKey: null,
    inAppKey: "qualityIssues",
  },
];

function NotificationSettingsPage() {
  const preferences = useQuery(api.userPreferences.get, {});
  const updatePreferences = useObservedMutation(api.userPreferences.update, {
    feature: "settings",
    operation: "update-notification-preferences",
    includeRequestId: true,
  });

  const [notifications, setNotifications] = useState(defaultNotifications);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const initializedRef = useRef(false);
  const lastSavedRef = useRef(JSON.stringify(defaultNotifications));
  const idleTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (preferences === undefined) return;
    const nextNotifications = preferences?.notifications ?? defaultNotifications;
    setNotifications(nextNotifications);
    lastSavedRef.current = JSON.stringify(nextNotifications);
    initializedRef.current = true;
  }, [preferences]);

  useEffect(() => {
    if (!initializedRef.current) return;

    const serialized = JSON.stringify(notifications);
    if (serialized === lastSavedRef.current) return;

    const timer = window.setTimeout(() => {
      setSaveState("saving");
      void updatePreferences({ notifications })
        .then(() => {
          lastSavedRef.current = serialized;
          setSaveState("saved");
          if (idleTimerRef.current) {
            window.clearTimeout(idleTimerRef.current);
          }
          idleTimerRef.current = window.setTimeout(() => {
            setSaveState("idle");
            idleTimerRef.current = null;
          }, 1200);
        })
        .catch(() => {
          setSaveState("error");
        });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [notifications, updatePreferences]);

  useEffect(() => {
    return () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const toggleEmail = (
    key: "clarificationResponses" | "versionChanges" | "mentions" | "weeklyDigest"
  ) => {
    setNotifications((prev) => ({
      ...prev,
      email: {
        ...prev.email,
        [key]: !prev.email[key],
      },
    }));
  };

  const toggleInApp = (
    key: "clarificationResponses" | "versionChanges" | "mentions" | "qualityIssues"
  ) => {
    setNotifications((prev) => ({
      ...prev,
      inApp: {
        ...prev.inApp,
        [key]: !prev.inApp[key],
      },
    }));
  };

  return (
    <SettingsPageShell
      title="Notifications"
      description="Choose what updates you receive and where we should deliver them."
    >
      <div className="space-y-6 max-w-4xl">
        <div className="overflow-hidden border border-border">
          <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_100px_100px] h-10 items-center border-b border-border px-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-neutral-dim">
              Notification type
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-neutral-dim text-center">
              Email
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.5px] text-neutral-dim text-center">
              In-app
            </p>
          </div>

          <div className="divide-y divide-border">
            {notificationRows.map((row) => (
              <NotificationRow
                key={row.key}
                row={row}
                notifications={notifications}
                onToggleEmail={toggleEmail}
                onToggleInApp={toggleInApp}
              />
            ))}
          </div>
        </div>

        <div className="min-h-5">
          {saveState === "saving" && <p className="text-xs text-muted-foreground">Saving…</p>}
          {saveState === "saved" && <p className="text-xs text-muted-foreground">Saved.</p>}
          {saveState === "error" && (
            <p className="text-xs text-destructive">Could not save. Try toggling again.</p>
          )}
        </div>
      </div>
    </SettingsPageShell>
  );
}

function NotificationRow({
  row,
  notifications,
  onToggleEmail,
  onToggleInApp,
}: {
  row: NotificationRow;
  notifications: typeof defaultNotifications;
  onToggleEmail: (
    key: "clarificationResponses" | "versionChanges" | "mentions" | "weeklyDigest"
  ) => void;
  onToggleInApp: (
    key: "clarificationResponses" | "versionChanges" | "mentions" | "qualityIssues"
  ) => void;
}) {
  const emailChecked = row.emailKey ? notifications.email[row.emailKey] : null;
  const inAppChecked = row.inAppKey ? notifications.inApp[row.inAppKey] : null;
  const emailOnChange = row.emailKey
    ? () =>
        onToggleEmail(
          row.emailKey as "clarificationResponses" | "versionChanges" | "mentions" | "weeklyDigest"
        )
    : null;
  const inAppOnChange = row.inAppKey
    ? () =>
        onToggleInApp(
          row.inAppKey as "clarificationResponses" | "versionChanges" | "mentions" | "qualityIssues"
        )
    : null;

  return (
    <div className="grid gap-3 px-5 py-4 md:grid-cols-[minmax(0,1fr)_100px_100px] md:items-center">
      <div className="flex flex-col gap-0.5">
        <span className="text-[13px] font-semibold text-foreground">{row.label}</span>
        <span className="text-xs text-neutral-dim">{row.description}</span>
      </div>

      <ChannelCell channelLabel="Email" checked={emailChecked} onCheckedChange={emailOnChange} />
      <ChannelCell channelLabel="In-app" checked={inAppChecked} onCheckedChange={inAppOnChange} />
    </div>
  );
}

function ChannelCell({
  channelLabel,
  checked,
  onCheckedChange,
}: {
  channelLabel: "Email" | "In-app";
  checked: boolean | null;
  onCheckedChange: (() => void) | null;
}) {
  if (checked === null || onCheckedChange === null) {
    return (
      <div className="flex items-center justify-start md:justify-center">
        <span className="text-xs text-muted-foreground">N/A</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between md:justify-center gap-3">
      <span className="text-xs font-medium text-muted-foreground md:hidden">{channelLabel}</span>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export const Route = createFileRoute("/settings/notifications")({
  component: NotificationSettingsPage,
});
